import React, { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../services/api'
import UploadZone from '../components/artifacts/UploadZone'
import UploadRow from '../components/artifacts/UploadRow'
import ArtifactCard from '../components/artifacts/ArtifactCard'
import { useArtifactUploads } from '../hooks/useArtifactUploads'

export default function Upload() {
  const [initiatives, setInitiatives] = useState([])
  const [selectedInitiativeId, setSelectedInitiativeId] = useState('')
  const [artifacts, setArtifacts] = useState([])
  const [loadingInitiatives, setLoadingInitiatives] = useState(true)
  const [loadingArtifacts, setLoadingArtifacts] = useState(true)
  const [error, setError] = useState(null)

  const { uploads, startUpload, dismiss } = useArtifactUploads()

  useEffect(() => {
    let cancelled = false
    api
      .get('/api/v1/initiatives')
      .then((data) => {
        if (cancelled) return
        setInitiatives(data)
        if (data.length > 0) setSelectedInitiativeId(String(data[0].id))
      })
      .catch((err) => setError(err.message))
      .finally(() => {
        if (!cancelled) setLoadingInitiatives(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const loadArtifacts = useCallback(async () => {
    setLoadingArtifacts(true)
    try {
      const data = await api.get('/api/v1/artifacts')
      setArtifacts(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoadingArtifacts(false)
    }
  }, [])

  useEffect(() => {
    loadArtifacts()
  }, [loadArtifacts])

  // Reload the artifact list whenever an in-flight upload finishes, so
  // finished items appear in the main list automatically. Track which
  // completed uploads we've already reconciled — this effect fires on EVERY
  // uploads change (including progress ticks of later uploads), and without
  // the guard an undismissed completed row re-triggered a fetch per tick.
  const reconciledRef = useRef(new Set())
  useEffect(() => {
    const fresh = uploads.filter(
      (u) => u.status === 'completed' && !reconciledRef.current.has(u.localId)
    )
    if (fresh.length === 0) return
    fresh.forEach((u) => reconciledRef.current.add(u.localId))
    loadArtifacts()
  }, [uploads, loadArtifacts])

  const handleFilesAccepted = (files) => {
    if (!selectedInitiativeId) return
    files.forEach((file) => startUpload(file, { initiativeId: Number(selectedInitiativeId) }))
  }

  const handleDelete = async (artifact) => {
    if (!confirm(`Delete "${artifact.name}"?`)) return
    try {
      await api.delete(`/api/v1/artifacts/${artifact.id}`)
      setArtifacts((prev) => prev.filter((a) => a.id !== artifact.id))
    } catch (err) {
      if (err instanceof ApiError) setError(err.message)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Upload</h2>
        <p className="mt-1 text-sm text-slate-500">
          Add documents, decks, and images to the shared knowledge base. Text is extracted,
          chunked, and embedded for search.
        </p>
      </div>

      {/* Initiative picker */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
          Initiative
        </label>
        {loadingInitiatives ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : initiatives.length === 0 ? (
          <p className="text-sm text-slate-500">No initiatives available. Ask an admin to create one.</p>
        ) : (
          <select
            value={selectedInitiativeId}
            onChange={(e) => setSelectedInitiativeId(e.target.value)}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
          >
            {initiatives.map((init) => (
              <option key={init.id} value={init.id}>
                {init.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Dropzone */}
      <UploadZone onFilesAccepted={handleFilesAccepted} disabled={!selectedInitiativeId} />

      {/* In-flight uploads */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            In progress
          </h3>
          {uploads.map((u) => (
            <UploadRow key={u.localId} upload={u} onDismiss={dismiss} />
          ))}
        </div>
      )}

      {/* Existing artifacts */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Your artifacts
        </h3>
        {loadingArtifacts && <p className="text-sm text-slate-500">Loading…</p>}
        {!loadingArtifacts && artifacts.length === 0 && (
          <p className="text-sm text-slate-500">Nothing uploaded yet.</p>
        )}
        {artifacts.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {artifacts.map((artifact) => (
              <ArtifactCard key={artifact.id} artifact={artifact} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
    </div>
  )
}
